import { useState, useEffect, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { Box, Button, Card, CardContent, CircularProgress, Tab, Tabs, Typography } from '@mui/material';
import SaveRoundedIcon from '@mui/icons-material/SaveRounded';
import { contentAPI, adminAPI } from '../services/api';
import { useToast } from '../context/ToastContext';
import ImageUpload from '../components/ImageUpload';
import FooterQuickLinksEditor from '../components/siteLabels/FooterQuickLinksEditor';
import FooterCategoriesEditor from '../components/siteLabels/FooterCategoriesEditor';
import { cleanFooterCategoriesForSave } from '../utils/siteLabelsEditors';
import InitiativesCardsEditor from '../components/siteLabels/InitiativesCardsEditor';
import AdminRichTextEditor from '../components/AdminRichTextEditor';
import { applySiteFavicon } from '../utils/applySiteFavicon';
import { applySiteDocumentTitle } from '../utils/applySiteDocumentTitle';
import { DEFAULT_ADMIN_DOCUMENT_TITLE } from '../utils/siteTitleDefaults';
import { PageHeader, EmptyState, DataTableCard } from '../components/ui';
import './Content.css';

/** Keys match backend site_settings; label prefix `[Section]` drives grouping tabs. */
const SITE_SETTING_KEYS = [
  {
    key: 'site_favicon_url',
    label: '[Brand] Site favicon (browser tab icon)',
    image: true,
    accept: 'image/png,image/jpeg,image/webp,image/gif,image/svg+xml,image/x-icon,.ico'
  },
  { key: 'site_brand_name', label: '[Brand] Site name (logo alt, admin sidebar)' },
  {
    key: 'site_public_logo_url',
    label: '[Brand] Logo URL (header & footer fallback if specific logos empty)',
    image: true
  },
  {
    key: 'site_header_logo_url',
    label: '[Brand] Header logo image (overrides public logo on header)',
    image: true
  },
  {
    key: 'site_footer_logo_url',
    label: '[Brand] Footer logo image (overrides public logo in footer)',
    image: true
  },
  { key: 'site_public_title', label: '[Brand] Public website browser tab title' },
  {
    key: 'site_meta_description',
    label: '[SEO] Default meta description (Google snippet fallback)',
    textarea: true
  },
  {
    key: 'site_public_url',
    label: '[SEO] Public site URL (canonical links & sitemap), e.g. https://digix7.com'
  },
  { key: 'site_admin_title', label: '[Brand] Admin panel browser tab title' },
  { key: 'stats_section_title', label: '[Home] Stats section title' },
  { key: 'stats_section_subtitle', label: '[Home] Stats section subtitle' },
  { key: 'stats_section_image_url', label: '[Home] Stats section media URL (image/video/YouTube)', image: true, media: true },
  { key: 'highlighted_events_title', label: '[Home] Highlighted Events title' },
  { key: 'highlighted_events_subtitle', label: '[Home] Highlighted Events subtitle' },
  { key: 'choose_experience_title', label: '[Home] Choose Your Experience title' },
  { key: 'choose_experience_subtitle', label: '[Home] Choose Your Experience subtitle' },
  { key: 'why_section_title', label: '[Home] Why section title' },
  { key: 'why_section_subtitle', label: '[Home] Why section subtitle' },
  { key: 'why_community_title', label: '[Home] Community Driven title' },
  { key: 'why_community_subtitle', label: '[Home] Community Driven tagline' },
  { key: 'why_community_image_url', label: '[Home] Community Driven photo', image: true },
  { key: 'special_events_title', label: '[Home] Special Events title' },
  { key: 'special_events_subtitle', label: '[Home] Special Events subtitle' },
  { key: 'blogs_section_title', label: '[Home] Latest Blogs title' },
  { key: 'blogs_section_subtitle', label: '[Home] Latest Blogs subtitle' },
  { key: 'home_latest_blog_ids', label: '[Home] Latest Blogs selected items', blogSelect: true },
  { key: 'partners_section_title', label: '[Home] Partners section title' },
  { key: 'partners_section_subtitle', label: '[Home] Partners section subtitle' },
  { key: 'events_hero_title', label: '[Events] Hero title' },
  { key: 'events_hero_subtitle', label: '[Events] Hero subtitle' },
  { key: 'events_hero_image', label: '[Events] Hero image', image: true },
  { key: 'blogs_hero_title', label: '[Blogs] Hero title' },
  { key: 'blogs_hero_subtitle', label: '[Blogs] Hero subtitle' },
  { key: 'blogs_hero_image', label: '[Blogs] Hero image', image: true },
  { key: 'about_hero_title', label: '[About] Orange banner title' },
  { key: 'about_hero_subtitle', label: '[About] Orange banner subtitle' },
  { key: 'about_hero_headline', label: '[About] Legacy headline (synced from banner title in About admin)' },
  { key: 'about_hero_description', label: '[About] Intro card paragraph', textarea: true },
  { key: 'about_hero_image', label: '[About] Intro card background image URL' },
  { key: 'about_mission_title', label: '[About] Mission section title' },
  { key: 'about_mission_text', label: '[About] Mission text (paragraphs)', textarea: true },
  { key: 'about_impact_title', label: '[About] Our Impact title' },
  { key: 'about_what_we_do_title', label: '[About] What We Do title' },
  { key: 'about_feature1_title', label: '[About] Feature 1 title' },
  { key: 'about_feature1_desc', label: '[About] Feature 1 description', textarea: true },
  { key: 'about_feature2_title', label: '[About] Feature 2 title' },
  { key: 'about_feature2_desc', label: '[About] Feature 2 description', textarea: true },
  { key: 'about_feature3_title', label: '[About] Feature 3 title' },
  { key: 'about_feature3_desc', label: '[About] Feature 3 description', textarea: true },
  { key: 'about_feature4_title', label: '[About] Feature 4 title' },
  { key: 'about_feature4_desc', label: '[About] Feature 4 description', textarea: true },
  { key: 'about_join_title', label: '[About] Join Community title' },
  { key: 'about_join_text', label: '[About] Join Community text', textarea: true },
  { key: 'contact_hero_title', label: '[Contact] Hero title' },
  { key: 'contact_hero_subtitle', label: '[Contact] Hero subtitle' },
  { key: 'contact_address', label: '[Contact] Address (lines with \\n)', textarea: true },
  { key: 'contact_phone', label: '[Contact] Phone (one per line)', textarea: true },
  { key: 'contact_email', label: '[Contact] Email (one per line)', textarea: true },
  { key: 'contact_hours', label: '[Contact] Office hours', textarea: true },
  { key: 'footer_mission', label: '[Footer] Mission/tagline', textarea: true },
  { key: 'footer_copyright', label: '[Footer] Copyright text' },
  {
    key: 'footer_events_categories',
    label: '[Footer] Events by Category (footer columns)',
    visualEditor: 'footerCategories'
  },
  {
    key: 'footer_quick_links',
    label: '[Footer] Quick Links',
    visualEditor: 'quickLinks'
  },
  { key: 'footer_regional_link_text', label: '[Footer] Regional offices link text' },
  { key: 'social_instagram', label: '[Footer] Instagram URL' },
  { key: 'social_linkedin', label: '[Footer] LinkedIn URL' },
  { key: 'social_facebook', label: '[Footer] Facebook URL' },
  { key: 'social_whatsapp', label: '[Footer] WhatsApp URL (footer icon & home floating button)' },
  { key: 'social_youtube', label: '[Footer] YouTube channel URL' },
  { key: 'booking_interactive_checklist_pdf_url', label: '[Bookings] Interactive Checklist PDF URL', pdf: true },
  {
    key: 'event_consultant_card_title',
    label: '[Event Detail] Consultant card heading (assign trek consultant in Users → Edit → Trek consultant)'
  },
  {
    key: 'event_consultant_card_subtitle',
    label: '[Event Detail] Consultant card description',
    textarea: true
  },
  {
    key: 'event_consultant_fallback_team_name',
    label: '[Event Detail] Consultant name fallback (when trek has no consultant user)'
  },
  { key: 'event_consultant_whatsapp_button_label', label: '[Event Detail] Consultant card — Whatsapp button' },
  { key: 'event_consultant_pdf_button_label', label: '[Event Detail] Consultant card — Get PDF button' },
  {
    key: 'event_consultant_callback_button_label',
    label: '[Event Detail] Consultant card — Request callback button'
  },
  { key: 'event_callback_modal_title', label: '[Event Detail] Callback popup title' },
  {
    key: 'event_consultant_pdf_url',
    label: '[Event Detail] Default brochure PDF URL (if trek has no brochure)',
    pdf: true
  },
  { key: 'event_special_offer_title', label: '[Event Detail] Special offer title' },
  { key: 'event_special_offer_body', label: '[Event Detail] Special offer body', textarea: true },
  { key: 'event_special_offer_enabled', label: '[Event Detail] Special offer enabled (true/false)' },
  { key: 'terms_page_title', label: '[Legal] Terms page title' },
  { key: 'terms_page_html', label: '[Legal] Terms page body (HTML)', textarea: true, legalHtml: true },
  { key: 'privacy_page_title', label: '[Legal] Privacy page title' },
  { key: 'privacy_page_html', label: '[Legal] Privacy page body (HTML)', textarea: true, legalHtml: true },
  { key: 'initiatives_hero_title', label: '[Initiatives] Page title' },
  { key: 'initiatives_hero_subtitle', label: '[Initiatives] Page subtitle' },
  {
    key: 'initiatives_cards',
    label: '[Initiatives] Content cards',
    visualEditor: 'initiativesCards'
  },
  { key: 'community_hero_title', label: '[Community] Page title' },
  { key: 'community_hero_subtitle', label: '[Community] Page subtitle' },
  { key: 'community_section_title', label: '[Community] Testimonials section title' },
];

const GROUP_ORDER = [
  'Brand',
  'Home',
  'Events',
  'Blogs',
  'About',
  'Contact',
  'Footer',
  'Legal',
  'Initiatives',
  'Community',
  'Event Detail',
  'Bookings',
  'General'
];

function groupKeysBySection() {
  return SITE_SETTING_KEYS.reduce((acc, item) => {
    const m = String(item.label || '').match(/^\[([^\]]+)\]/);
    const group = m?.[1] || 'General';
    if (!acc[group]) acc[group] = [];
    acc[group].push(item);
    return acc;
  }, {});
}

const SiteSettingsForm = ({
  settings,
  isLoading,
  onSave,
  isSaving,
  blogOptions = [],
  activeGroup,
}) => {
  const [local, setLocal] = useState(() => {
    const o = {};
    SITE_SETTING_KEYS.forEach(({ key }) => { o[key] = settings[key] ?? ''; });
    return o;
  });
  useEffect(() => {
    const o = {};
    SITE_SETTING_KEYS.forEach(({ key }) => { o[key] = settings[key] ?? ''; });
    setLocal(o);
  }, [settings]);

  const handleChange = (key, value) => {
    setLocal((prev) => ({ ...prev, [key]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const settingsArray = SITE_SETTING_KEYS.map(({ key }) => {
      let value = local[key] ?? '';
      if (key === 'footer_events_categories') {
        value = cleanFooterCategoriesForSave(value);
      }
      return { key, value };
    });
    onSave(settingsArray);
  };

  const parseSelectedBlogIds = (raw) => {
    try {
      const parsed = JSON.parse(raw || '[]');
      return Array.isArray(parsed) ? parsed.map(String) : [];
    } catch {
      return [];
    }
  };

  const groupedKeys = useMemo(() => groupKeysBySection(), []);
  const sectionItems = groupedKeys[activeGroup] || [];

  if (isLoading) {
    return (
      <Card>
        <CardContent>
          <div className="ops-loading-box">
            <CircularProgress size={28} sx={{ color: 'primary.main' }} />
            <Typography variant="body2" color="text.secondary">Loading site labels…</Typography>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card component="form" onSubmit={handleSubmit} className="site-labels-form">
      <CardContent className="content-form-pad">
      <Typography variant="body2" color="text.secondary" className="content-form-intro">
        Edit site-wide labels and copy by section. Changes apply on the public site after you save.
      </Typography>
      {activeGroup === 'Footer' && (
        <p className="site-labels-friendly-banner">
          Footer links and category columns use simple forms — you do not need to write code. Fill in the fields, then
          click <strong>Save all site labels</strong> at the bottom.
        </p>
      )}
      {activeGroup === 'Initiatives' && (
        <p className="site-labels-friendly-banner">
          Add or edit initiative cards with the form below — no technical format required.
        </p>
      )}
      {activeGroup === 'Event Detail' && (
        <p className="site-labels-friendly-banner">
          The <strong>consultant person</strong> (photo, name, phone, email) is chosen per trek in{' '}
          <strong>Treks → Edit → Consultant</strong> (a user from your team). Use the fields below only for
          card wording and button labels.
        </p>
      )}
      <div className="content-settings-list">
        <Box className="content-settings-group" sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, overflow: 'hidden', bgcolor: 'background.paper' }}>
          <Typography variant="subtitle2" fontWeight={700} className="content-settings-group-title" sx={{ m: 0, px: 1.75, py: 1.25, bgcolor: '#f8fafc', borderBottom: '1px solid', borderColor: 'divider' }}>
            {activeGroup}
          </Typography>
          <DataTableCard sx={{ boxShadow: 'none', borderRadius: 0 }}>
          <table className="content-settings-table">
            <thead>
              <tr>
                <th>Field</th>
                <th>Value</th>
              </tr>
            </thead>
            <tbody>
              {sectionItems.map(({ key, label, textarea, image, media, pdf, blogSelect, accept: fieldAccept, legalHtml, visualEditor }) => (
                <tr key={key} className={visualEditor ? 'site-labels-visual-row-full' : ''}>
                  <td className="content-settings-label-cell">{label.replace(/^\[[^\]]+\]\s*/, '')}</td>
                  <td className="content-settings-value-cell">
                    {visualEditor === 'quickLinks' ? (
                      <FooterQuickLinksEditor
                        value={local[key] ?? ''}
                        onChange={(json) => handleChange(key, json)}
                      />
                    ) : visualEditor === 'footerCategories' ? (
                      <FooterCategoriesEditor
                        value={local[key] ?? ''}
                        onChange={(json) => handleChange(key, json)}
                      />
                    ) : visualEditor === 'initiativesCards' ? (
                      <InitiativesCardsEditor
                        value={local[key] ?? ''}
                        onChange={(json) => handleChange(key, json)}
                      />
                    ) : legalHtml ? (
                      <div className="site-labels-visual">
                        <AdminRichTextEditor
                          compact
                          value={local[key] ?? ''}
                          onChange={(html) => handleChange(key, html)}
                          hint="Use the toolbar for bold, lists, and links. This content appears on the public site."
                          placeholder="Type content here…"
                        />
                      </div>
                    ) : pdf ? (
                      <ImageUpload
                        label=""
                        value={local[key] ?? ''}
                        onChange={(value) => handleChange(key, value)}
                        accept="application/pdf"
                        uploadImage={async (file) => {
                          const res = await adminAPI.uploadPdf(file);
                          return res?.data?.data?.url || '';
                        }}
                      />
                    ) : image ? (
                      <ImageUpload
                        label=""
                        value={local[key] ?? ''}
                        onChange={(value) => handleChange(key, value)}
                        accept={fieldAccept || (media ? 'image/*,video/*' : 'image/*')}
                        uploadImage={async (file) => {
                          const res = media ? await adminAPI.uploadMedia(file) : await adminAPI.uploadImage(file);
                          return res?.data?.data?.url || '';
                        }}
                      />
                    ) : blogSelect ? (
                      (() => {
                        const selectedIds = new Set(parseSelectedBlogIds(local[key]));
                        return (
                          <div className="blog-home-checkbox-list" role="group" aria-label="Select blogs to show on Home">
                            {blogOptions.length === 0 ? (
                              <div className="form-hint">No blogs found in admin.</div>
                            ) : (
                              blogOptions.map((blog) => {
                                const idStr = String(blog.id);
                                const checked = selectedIds.has(idStr);
                                return (
                                  <label key={idStr} className="blog-home-checkbox-item">
                                    <input
                                      type="checkbox"
                                      checked={checked}
                                      onChange={(e) => {
                                        const next = new Set(selectedIds);
                                        if (e.target.checked) next.add(idStr);
                                        else next.delete(idStr);
                                        handleChange(key, JSON.stringify(Array.from(next)));
                                      }}
                                    />
                                    <span className="blog-home-checkbox-title">
                                      {blog.title || `Blog ${idStr}`}
                                    </span>
                                  </label>
                                );
                              })
                            )}
                          </div>
                        );
                      })()
                    ) : textarea ? (
                      <textarea
                        id={key}
                        value={local[key] ?? ''}
                        onChange={(e) => handleChange(key, e.target.value)}
                        className="input site-labels-textarea"
                        rows={
                          key === 'about_mission_text' || key === 'about_join_text'
                              ? 12
                              : 8
                        }
                      />
                    ) : (
                      <input
                        id={key}
                        type="text"
                        value={local[key] ?? ''}
                        onChange={(e) => handleChange(key, e.target.value)}
                        className="input"
                      />
                    )}
                    {blogSelect && (
                      <p className="form-hint">Tick blogs to show on the Home page Latest Blogs section (order follows selection order).</p>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </DataTableCard>
        </Box>
      </div>
      <Box className="form-actions content-mt-6" sx={{ display: 'flex', justifyContent: 'flex-end', pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
        <Button type="submit" variant="contained" disabled={isSaving} startIcon={<SaveRoundedIcon />}>
          {isSaving ? 'Saving...' : 'Save all site labels'}
        </Button>
      </Box>
      </CardContent>
    </Card>
  );
};

const SiteLabels = () => {
  const queryClient = useQueryClient();
  const { showToast } = useToast();
  const grouped = useMemo(() => groupKeysBySection(), []);
  const sectionNames = useMemo(() => {
    const keys = Object.keys(grouped);
    return [...keys].sort((a, b) => {
      const ia = GROUP_ORDER.indexOf(a);
      const ib = GROUP_ORDER.indexOf(b);
      const sa = ia === -1 ? 999 : ia;
      const sb = ib === -1 ? 999 : ib;
      if (sa !== sb) return sa - sb;
      return a.localeCompare(b);
    });
  }, [grouped]);

  const [activeGroup, setActiveGroup] = useState(() => sectionNames[0] || 'Home');

  useEffect(() => {
    if (sectionNames.length && !sectionNames.includes(activeGroup)) {
      setActiveGroup(sectionNames[0]);
    }
  }, [sectionNames, activeGroup]);

  const { data, isLoading, error } = useQuery(['site-labels', 'settings'], async () => {
    const response = await contentAPI.getSiteSettings();
    return response.data;
  });

  const { data: blogOptions = [] } = useQuery(['site-labels', 'blogs'], async () => {
    try {
      const response = await contentAPI.getBlogs();
      return Array.isArray(response?.data?.data) ? response.data.data : [];
    } catch {
      return [];
    }
  });

  const saveMutation = useMutation(
    (settingsArray) => contentAPI.updateSiteSettings(settingsArray),
    {
      onSuccess: (_data, settingsArray) => {
        queryClient.invalidateQueries(['site-labels', 'settings']);
        queryClient.invalidateQueries(['site-settings-branding']);
        const byKey = (key) =>
          Array.isArray(settingsArray)
            ? settingsArray.find((s) => s.key === key)?.value
            : '';
        const fav = byKey('site_favicon_url');
        if (fav) applySiteFavicon(fav);
        const adminTitle = byKey('site_admin_title');
        applySiteDocumentTitle(adminTitle, DEFAULT_ADMIN_DOCUMENT_TITLE);
        showToast({ message: 'Site labels saved successfully.', severity: 'success', duration: 5000 });
      },
      onError: (err) => {
        const msg =
          err?.response?.data?.message ||
          err?.message ||
          'Could not save site labels. Please try again.';
        showToast({ message: msg, severity: 'error', duration: 6000 });
      },
    }
  );

  const settings = data?.data || {};

  return (
    <div className="content-page module-page">
      <PageHeader
        title="Site Labels"
        subtitle="Titles, subtitles, and copy used across the website"
      />

      <Card sx={{ mb: 2 }}>
        <Tabs
          value={activeGroup}
          onChange={(_, value) => setActiveGroup(value)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{ px: 1, borderBottom: 1, borderColor: 'divider' }}
        >
          {sectionNames.map((name) => (
            <Tab key={name} value={name} label={name} />
          ))}
        </Tabs>
      </Card>

      <div className="content-area">
        {error && (
          <EmptyState
            title="Error loading site labels"
            description="Please refresh and try again."
            sx={{ borderColor: 'error.light' }}
          />
        )}
        {!error && isLoading ? (
          <Card>
            <CardContent>
              <div className="ops-loading-box">
                <CircularProgress size={28} sx={{ color: 'primary.main' }} />
                <Typography variant="body2" color="text.secondary">Loading site labels…</Typography>
              </div>
            </CardContent>
          </Card>
        ) : !error ? (
          <SiteSettingsForm
            settings={settings}
            isLoading={false}
            onSave={(arr) => saveMutation.mutate(arr)}
            isSaving={saveMutation.isLoading}
            blogOptions={blogOptions}
            activeGroup={activeGroup}
          />
        ) : null}
      </div>
    </div>
  );
};

export default SiteLabels;
